import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, TextField, Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useQuery } from '@tanstack/react-query';

import { colors, primaryButtonSx, secondaryButtonSx, dialogActionsSx, typo } from '../../../../theme/tokens';
import { WriteButton } from '../../../../components/ui';
import { PERMISSION_META, PermissionCode, PermissionGroup } from '../../../../auth/permissionCatalog';
import { RbacRole, RoleInput, rolesV2Api } from '../../../../api/rolesV2Api';

export type EditorMode = 'create' | 'edit' | 'view';

interface Props {
  open: boolean;
  mode: EditorMode;
  /** Prefill source for edit/view mode. */
  role?: RbacRole | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: RoleInput) => void;
}

const EMPTY: RoleInput = { role_name: '', description: '', permissions: [] };

/** Canonical group render order (architecture R5 / design §8.2). */
const GROUP_ORDER: PermissionGroup[] = [
  'Workspaces',
  'Insights',
  'Publishing & Promotion',
  'Admin & Operations',
  'Layout',
];

/**
 * Create / edit / view a ROLE (persona). A role bundles one or more of the
 * 25 grantable permissions — rendered as a checklist grouped into the
 * sections from `PERMISSION_META` (Workspaces / Insights / Publishing &
 * Promotion / Admin & Operations / Layout), each permission's helper text =
 * its description.
 * There is NO way to create/rename/delete a permission here — the catalog
 * is engineering-defined; ANY role may hold ANY permission, including
 * `hide_chrome` and `prod_option` (decisions §5).
 *
 * The catalog itself comes from `GET /permissions` (so a drifted label
 * self-corrects) — `PERMISSION_META` supplies only the group/order used to
 * render the checklist's structure, per architecture R5's grouping.
 *
 * `view` mode (used for the locked system roles) shows everything read-only:
 * their permission set is viewable but the lock is respected — no field or
 * checkbox is editable and there is no Save.
 */
const RolePersonaEditorDialog: React.FC<Props> = ({ open, mode, role, saving, onClose, onSubmit }) => {
  const [form, setForm] = useState<RoleInput>(EMPTY);
  const readOnly = mode === 'view';

  // Editor owns its catalog fetch so it shows a real loading/error state.
  const permsQuery = useQuery({
    queryKey: ['rbac-v2', 'permissions'],
    queryFn: () => rolesV2Api.listPermissions(),
    enabled: open,
  });
  const permissions = permsQuery.data ?? [];

  // Driven by the LIVE catalog response (self-correcting — a soft-retired
  // permission simply stops coming back and drops off the checklist);
  // `PERMISSION_META` supplies ONLY the group/order used to render its
  // structure, never which codes exist.
  const groupedPermissions = useMemo(() => {
    return GROUP_ORDER.map((group) => ({
      group,
      items: permissions
        .filter((p) => PERMISSION_META[p.permission_code]?.group === group)
        .sort((a, b) => (PERMISSION_META[a.permission_code]?.order ?? 0) - (PERMISSION_META[b.permission_code]?.order ?? 0))
        .map((p) => ({ code: p.permission_code, label: p.label, description: p.description })),
    })).filter((g) => g.items.length > 0);
  }, [permissions]);

  useEffect(() => {
    if (!open) return;
    setForm(
      (mode === 'edit' || mode === 'view') && role
        ? { role_name: role.role_name, description: role.description ?? '', permissions: [...role.permissions] }
        : EMPTY,
    );
  }, [open, mode, role]);

  const togglePermission = (code: PermissionCode) =>
    setForm((p) => ({
      ...p,
      permissions: p.permissions.includes(code)
        ? p.permissions.filter((c) => c !== code)
        : [...p.permissions, code],
    }));

  const canSave = !readOnly && !!form.role_name.trim() && !saving;
  // Non-blocking warning: hide_chrome hides the top-bar chrome and lands
  // the user on Insights — without a paired insights permission there's
  // nowhere for them to land (adversarial review P1; the redirect chain
  // itself falls through to /access-denied rather than looping, but a role
  // shaped like this is still almost certainly a mistake). Save stays
  // allowed — this mirrors the zero-permission warning's non-blocking posture.
  const hideChromeWithoutInsights =
    !readOnly &&
    form.permissions.includes('hide_chrome') &&
    !form.permissions.includes('insights_manager') &&
    !form.permissions.includes('insights_client');
  const title =
    mode === 'view' ? `Role — ${role?.role_name ?? ''}`
    : mode === 'edit' ? `Edit role — ${role?.role_name ?? ''}`
    : 'Create role';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 18, display: 'flex', alignItems: 'center', gap: 1 }}>
        {readOnly && <LockIcon sx={{ fontSize: 18, color: colors.textTertiary }} />}
        {title}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {readOnly && (
            <Alert severity="info" sx={{ borderRadius: '8px' }}>
              This is a system role — its name, description, and permissions are locked.
            </Alert>
          )}

          <TextField
            label="Role name"
            size="small"
            required
            value={form.role_name}
            onChange={(e) => setForm((p) => ({ ...p, role_name: e.target.value }))}
            placeholder="e.g. Operations"
            disabled={readOnly}
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            label="Description"
            size="small"
            multiline
            rows={2}
            value={form.description ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="What is this persona for?"
            disabled={readOnly}
          />

          {/* Permission checklist — the FIXED catalog, grouped. No add/remove-from-catalog. */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ ...typo.sectionTitle, fontSize: 14 }}>Permissions</Typography>
              <Typography sx={{ ...typo.tinyText, color: colors.textTertiary }}>
                {form.permissions.length} selected · fixed catalog
              </Typography>
            </Box>
            <Typography sx={{ ...typo.smallText, color: colors.textSecondary, mb: 1 }}>
              A role grants exactly the permissions checked below. Permissions are defined by
              engineering and can’t be created or renamed here.
            </Typography>

            {permsQuery.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={22} sx={{ color: colors.adminAccent }} />
              </Box>
            ) : permsQuery.isError ? (
              <Alert severity="error" sx={{ borderRadius: '8px' }}>Couldn’t load permissions — try reopening.</Alert>
            ) : (
              <>
                {!readOnly && form.permissions.length === 0 && (
                  <Alert severity="warning" sx={{ borderRadius: '8px', mb: 1 }}>
                    This role has no permissions — users assigned to it will have no access beyond sign-in.
                  </Alert>
                )}
                {hideChromeWithoutInsights && (
                  <Alert severity="warning" sx={{ borderRadius: '8px', mb: 1 }}>
                    Hide chrome without an Insights permission leaves users nowhere to land — pair it
                    with Insights (client) or (manager).
                  </Alert>
                )}
                <Box sx={{ border: `1px solid ${colors.borderLight}`, borderRadius: '8px', p: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {groupedPermissions.map(({ group, items }) => (
                    <Box key={group}>
                      <Typography
                        sx={{ ...typo.tinyText, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 1, mb: 0.25 }}
                      >
                        {group}
                      </Typography>
                      {items.map((perm) => {
                        const checked = form.permissions.includes(perm.code);
                        return (
                          <Box key={perm.code} sx={{ px: 1, py: 0.5, borderRadius: '6px', '&:hover': readOnly ? {} : { backgroundColor: colors.adminAccentSubtle } }}>
                            <FormControlLabel
                              sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                              disabled={readOnly}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={checked}
                                  onChange={() => togglePermission(perm.code)}
                                  sx={{ pt: 0.25 }}
                                />
                              }
                              label={
                                <Box sx={{ ml: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                                      {perm.label}
                                    </Typography>
                                    <Chip label={perm.code} size="small" sx={{ ...typo.mono, height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }} />
                                  </Box>
                                  <Typography sx={{ ...typo.smallText, color: colors.textSecondary }}>
                                    {perm.description}
                                  </Typography>
                                </Box>
                              }
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} sx={{ ...secondaryButtonSx }}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <WriteButton
            ignoreEnvReadOnly
            variant="contained"
            disabled={!canSave}
            onClick={() => onSubmit({ role_name: form.role_name.trim(), description: (form.description ?? '').trim(), permissions: form.permissions })}
            sx={primaryButtonSx}
          >
            {saving ? <CircularProgress size={18} sx={{ color: colors.textPrimary }} /> : mode === 'edit' ? 'Save changes' : 'Create role'}
          </WriteButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RolePersonaEditorDialog;
